import { ControlledInput, ControlledInputProps } from "@hm/fundamental"
import { useCallback, useEffect, useRef, useState } from "react"
import { FieldError, RegisterOptions, useFormContext } from "react-hook-form"

export interface FormattedNumberInputRHFProps
	extends Omit<
		ControlledInputProps,
		| "name"
		| "value"
		| "onChange"
		| "onBlur"
		| "onFocus"
		| "error"
		| "isTouched"
		| "type"
	> {
	name: string
	locale: string
	maxFractionDigits?: number
	rules?: RegisterOptions
}

export const FormattedNumberInputRHF: React.FC<
	FormattedNumberInputRHFProps
> = ({ name, locale, maxFractionDigits = 3, rules, ...inputProps }) => {
	const REF_NUMBER = 1234.5

	const thousandSeparator =
		new Intl.NumberFormat(locale)
			.formatToParts(REF_NUMBER)
			.find(p => p.type === "group")?.value ?? ","
	const decimalSeparator =
		new Intl.NumberFormat(locale)
			.formatToParts(REF_NUMBER)
			.find(p => p.type === "decimal")?.value ?? "."

	const {
		register,
		setValue,
		watch,
		formState: { errors, touchedFields },
	} = useFormContext()
	register(name, rules)

	const fieldValue = watch(name) as number | undefined
	const fieldError = errors[name]
	const { message: errorMessage, type: errorType } =
		(fieldError as FieldError) ?? {}
	const isTouched = !!touchedFields[name]

	const isFocused = useRef(false)

	const formatDisplay = useCallback(
		(value: number) =>
			value.toLocaleString(locale, {
				minimumFractionDigits: maxFractionDigits,
				maximumFractionDigits: maxFractionDigits,
			}),
		[locale, maxFractionDigits],
	)

	const parseNumber = useCallback(
		(inputValue: string): number => {
			if (inputValue.trim() === "") return 0
			const normalized = inputValue
				.replaceAll(thousandSeparator, "")
				.replace(decimalSeparator, ".")
			const parsed = Number.parseFloat(normalized)
			return Number.isNaN(parsed) ? 0 : parsed
		},
		[thousandSeparator, decimalSeparator],
	)

	const [displayValue, setDisplayValue] = useState(() =>
		formatDisplay(fieldValue ?? 0),
	)

	// Sync display when field value changes externally (e.g. via setValue from outside)
	// Skip when user is actively typing to avoid overwriting the raw input string
	useEffect(() => {
		if (!isFocused.current) {
			setDisplayValue(formatDisplay(fieldValue ?? 0))
		}
	}, [fieldValue, formatDisplay])

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const inputValue = e.target.value

		let formattedValue = inputValue.replaceAll(
			new RegExp(String.raw`[^\d${decimalSeparator}]`, "g"),
			"",
		)

		const parts = formattedValue.split(decimalSeparator)
		if (parts.length > 2) {
			formattedValue =
				parts[0] + decimalSeparator + parts.slice(1).join("")
		}

		const indexOfDecimalSeparator = formattedValue.indexOf(decimalSeparator)
		if (indexOfDecimalSeparator > 0 && maxFractionDigits) {
			formattedValue = formattedValue.slice(
				0,
				indexOfDecimalSeparator + 1 + maxFractionDigits,
			)
		}

		setValue(name, parseNumber(formattedValue), {
			shouldValidate: true,
			shouldDirty: true,
		})
		setDisplayValue(formattedValue)
	}

	const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		isFocused.current = false
		const parsedValue = parseNumber(e.target.value)
		setDisplayValue(formatDisplay(parsedValue))
		setValue(name, parsedValue, {
			shouldValidate: true,
			shouldTouch: true,
		})
	}

	const handleFocus = () => {
		isFocused.current = true
		const parsedNumValue = (fieldValue as number) ?? 0
		const unformatted = parsedNumValue
			.toLocaleString(locale, {
				useGrouping: false,
				maximumFractionDigits: maxFractionDigits,
			})
			.replaceAll(
				new RegExp(String.raw`[^\d${decimalSeparator}]`, "g"),
				"",
			)
		setDisplayValue(unformatted)
	}

	return (
		<ControlledInput
			{...inputProps}
			name={name}
			type="text"
			inputMode="decimal"
			autoComplete="off"
			value={displayValue}
			onChange={handleChange}
			onBlur={handleBlur}
			onFocus={handleFocus}
			error={errorMessage}
			isTouched={isTouched}
			aria-invalid={!!errorType}
		/>
	)
}
